import * as React from "react";
import { Gapped, Input, Link } from "react-ui";

import {
    PAGER_HOT_KEY_MOVE_ON_INPUT,
    PAGER_INPUT_SUBMIT,
    PAGER_INTERVAL_CLICK,
    PAGER_NEXT_CLICK,
    PAGER_PREVIOUS_CLICK,
} from "../../metricsAction";
import { emptyRef, nullElement } from "../../TypingUtils";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { clamp } from "../../../../../Common/IntRangeUtils";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { isEnterKey } from "../../../Helpers/KeyboardUtils";
import { useKeyboardShortcut } from "../../Commons/Keyboard/UseKeyboardShortcut";
import { InternalIcon } from "../../Icons/Icon";

import { ErrorContainer, PagerSegment, TextContainer } from "./Pager.styles";

interface GoToPageProps {
    onSubmit: () => void;
    onChangeValue: (nextValue: string) => void;
    value: string;
    error: boolean;
    focusByHotkey?: boolean;
}

function GoToPage(props: GoToPageProps): JSX.Element {
    const input = React.useRef<Input>(emptyRef);
    const track = useMetrics();

    useKeyboardShortcut(
        "Alt+G",
        React.useCallback(() => {
            if (props.focusByHotkey) {
                track(PAGER_HOT_KEY_MOVE_ON_INPUT);
                input.current?.focus();
            }
        }, [props.focusByHotkey, input, track]),
        true
    );

    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (isEnterKey(e)) {
            props.onSubmit();
        }
    };

    return (
        <>
            <TextContainer>Перейти к строке</TextContainer>
            <Input
                ref={input}
                error={props.error}
                placeholder={"Alt+G"}
                value={props.value}
                width={100}
                onKeyPress={handleKeyPress}
                onValueChange={props.onChangeValue}
            />
            {props.error && <ErrorContainer>Такой строки нет</ErrorContainer>}
        </>
    );
}

interface PageLinkProps {
    onClick: (pageNumber: number) => void;
    current: boolean;
    page: number;
    children: React.ReactNode;
}

function PageLink(props: PageLinkProps): JSX.Element {
    const handleOnClick = React.useCallback(() => props.onClick(props.page), [props.page]);

    return (
        <Link disabled={props.current} onClick={handleOnClick}>
            {props.children}
        </Link>
    );
}

export interface PagerProps {
    limit?: number;
    currentPage: number;
    instanceCount: number;
    instancesPerPage: number;
    onGoToPage: (pageNumber: number) => void;
    focusByHotkey?: boolean;
    loading?: boolean;
}

export function Pager(props: PagerProps): JSX.Element | null {
    const [gotoPageError, setGotoPageError] = React.useState(false);
    const [gotoPageValue, setGotoPageValue] = React.useState("");

    const track = useMetrics();
    const { currentPage, limit = 8, instanceCount, instancesPerPage } = props;
    const pageCount = Math.ceil(instanceCount / instancesPerPage);

    const cleanInput = () => {
        setGotoPageValue("");
        setGotoPageError(false);
    };

    React.useEffect(() => cleanInput(), [currentPage, instanceCount]);

    const handleOnClickPageLink = (page: number): void => {
        track(PAGER_INTERVAL_CLICK, { currentPage: currentPage, page: page });
        props.onGoToPage(page);
    };

    const handleChangeValue = (nextValue: string): void => {
        setGotoPageValue(nextValue);
        setGotoPageError(false);
    };

    const handleOnSubmit = (): void => {
        track(PAGER_INPUT_SUBMIT, { currentPage: currentPage, row: gotoPageValue });
        const parsedValue = Number(gotoPageValue);
        const isPageExists = isFinite(parsedValue) && parsedValue > 0 && parsedValue <= instanceCount;

        if (isPageExists) {
            setGotoPageValue("");
            props.onGoToPage(Math.ceil(parsedValue / instancesPerPage));
        }
        setGotoPageError(!isPageExists);
    };

    const handleOnClickNext = (): void => {
        track(PAGER_NEXT_CLICK, { compact: pageCount > limit });
        props.onGoToPage(currentPage + 1);
        cleanInput();
    };

    const handleOnClickPrevious = (): void => {
        track(PAGER_PREVIOUS_CLICK, { compact: pageCount > limit });
        props.onGoToPage(currentPage - 1);
        cleanInput();
    };

    const renderInterval = (start: number, end: number) => (start === end ? `${start}` : `${start}-${end}`);

    const renderRegularIntervals = () => (
        <Gapped gap={20}>
            <div>Строки:</div>
            {[...IterUtils.range(1, pageCount)].map(page => (
                <PageLink current={currentPage === page} key={page} page={page} onClick={handleOnClickPageLink}>
                    {renderInterval(
                        clamp((page - 1) * instancesPerPage + 1, 1, instanceCount),
                        clamp(page * instancesPerPage, 1, instanceCount)
                    )}
                </PageLink>
            ))}
        </Gapped>
    );

    const renderCompactIntervals = (): JSX.Element => (
        <Gapped gap={20}>
            {`Строки ${(currentPage - 1) * instancesPerPage + 1} — ${Math.min(
                currentPage * instancesPerPage,
                instanceCount
            )} из ${instanceCount}`}
        </Gapped>
    );

    if (pageCount <= 1) {
        return nullElement;
    }

    const disabledByLoader = props.loading ?? false;

    return (
        <div>
            <PagerSegment>{pageCount > limit ? renderCompactIntervals() : renderRegularIntervals()}</PagerSegment>
            <PagerSegment>
                <Gapped gap={20}>
                    <Link
                        disabled={currentPage === 1 || disabledByLoader}
                        onClick={handleOnClickPrevious}
                        data-tid="pager-previous-link">
                        <InternalIcon name="ArrowCLeftIcon" /> Предыдущая
                    </Link>
                    <Link
                        disabled={currentPage === pageCount || disabledByLoader}
                        onClick={handleOnClickNext}
                        data-tid="pager-next-link">
                        Следующая <InternalIcon name="ArrowCRightIcon" />
                    </Link>
                </Gapped>
            </PagerSegment>
            {pageCount > limit && (
                <PagerSegment>
                    <Gapped gap={20}>
                        <GoToPage
                            error={gotoPageError}
                            focusByHotkey={props.focusByHotkey}
                            value={gotoPageValue}
                            onChangeValue={handleChangeValue}
                            onSubmit={handleOnSubmit}
                        />
                    </Gapped>
                </PagerSegment>
            )}
        </div>
    );
}
